const VOUCHER_VARIABLE_REGEX = /(\{VOUCHER_BUTTON)|(\{CODE_BTN)|(VOUCHER_NUMBER)|(CODE_NBR)|{var=voucher\.[a-zA-Z_]+}/;
const VOUCHER_WIDGET_REGEX = /data-widgettype="[^"]*voucher[^"]*"/i;

const TRANSLATION_KEY_REGEX = /LANG_[a-zA-Z0-9]+/g;
const VOUCHER_CONDITION_SEGMENT = 'voucher_is_configured';

interface VoucherScanTranslation {
  key: string;
  value?: unknown;
}

/**
 * True when the HTML displays a voucher in any language: voucher widget or
 * variable in the HTML itself, or in a translation it references (orphan
 * translations are ignored). Same rule as the backend sync propagation.
 */
export const htmlHasVoucher = (
  html: string,
  translations: ReadonlyArray<VoucherScanTranslation> = [],
): boolean => {
  if (VOUCHER_WIDGET_REGEX.test(html) || VOUCHER_VARIABLE_REGEX.test(html)) {
    return true;
  }

  const keys = new Set((html.match(TRANSLATION_KEY_REGEX) ?? []).map((key) => key.slice(5)));
  return translations.some(
    (translation) => keys.has(translation.key.split('.')[0])
      && VOUCHER_VARIABLE_REGEX.test(String(translation.value ?? '')),
  );
};

const isVoucherConditioned = (segments: string | null): boolean => {
  try {
    return (JSON.parse(segments || '[]') as { id?: string }[][]).some(
      (andBlock) => Array.isArray(andBlock) && andBlock.some((segment) => segment?.id === VOUCHER_CONDITION_SEGMENT),
    );
  } catch {
    return false;
  }
};

/**
 * True when every voucher of the element, in any language, sits inside a
 * block conditioned by the `voucher_is_configured` segment.
 */
export const hasOnlyConditionalVoucher = (
  root: Element,
  translations: ReadonlyArray<VoucherScanTranslation> = [],
): boolean => {
  const remaining = root.cloneNode(true) as Element;
  const conditionalBlocks = Array.from(remaining.querySelectorAll('[s-segments]'))
    .filter((block) => isVoucherConditioned(block.getAttribute('s-segments')));
  if (!conditionalBlocks.some((block) => htmlHasVoucher(block.outerHTML, translations))) {
    return false;
  }

  conditionalBlocks.forEach((block) => block.remove());
  return !htmlHasVoucher(remaining.outerHTML, translations);
};
